<template>
  <v-card 
    class="mb-4 hover-card"
    :class="{
      'card-upcoming': isUpcoming,
      'card-completed': isCompleted
    }"
    elevation="1"
    style="border-radius: 0; border: 1px solid #e5e7eb; background: #ffffff;"
    hover
  >
    <v-card-text class="card-text-pad">
      <div class="card-body">
        <!-- Dozent Bild oder Icon -->
        <div class="card-media" style="position: relative;">
          <!-- Bild wenn vorhanden -->

          <v-img
            v-if="webinar.dozentImage"
            :src="webinar.dozentImage"
            :alt="webinar.dozent"
            :width="baseStore?.isDesktop ? 80 : 52"
            :height="baseStore?.isDesktop ? 80 : 52"
            cover
          />
          <!-- Icon als Fallback -->
          <v-avatar
            v-else
            :size="baseStore?.isDesktop ? 80 : 52"
            color="#e0e0e0"
          >
            <v-icon :size="baseStore?.isDesktop ? 48 : 30" color="#757575">mdi-account-circle</v-icon>
          </v-avatar>

          <!-- Webinar Icon als Badge -->
          <v-avatar
            color="#e5e7eb"
            :size="baseStore?.isDesktop ? 32 : 22"
            class="webinar-badge"
            style="position: absolute; bottom: -4px; right: -4px; border: 2px solid white;"
          >
            <v-icon color="#4b5563" :size="baseStore?.isDesktop ? 18 : 13">{{ webinar.icon }}</v-icon>
          </v-avatar>
        </div>

        <!-- Content -->
        <div class="flex-grow-1">
          <div class="card-head mb-2">
            <h3 :class="baseStore?.isDesktop ? 'text-h5 font-weight-bold' : 'card-title-mobile'" class="card-title mb-0 card-title-wrap">
              {{ webinar.title }}
            </h3>
            <v-chip
              v-if="webinar.isDozentTeilnahme"
              size="small"
              color="deep-purple-darken-1"
              variant="tonal"
            >
              Hier bin ich Dozent
            </v-chip>
            <v-chip
              v-else-if="isCompleted"
              size="small"
              color="grey-darken-1"
              variant="tonal"
            >
              Abgeschlossen
            </v-chip>
            <v-chip
              v-else-if="isUpcoming"
              size="small"
              color="green-darken-1"
              variant="tonal"
            >
              Demnächst
            </v-chip>
          </div>
          <p class="text-body-2 text-grey-darken-1 mb-3">
            {{ webinar.description }}
          </p>
          
          <!-- Meta-Informationen -->
          <v-divider class="my-3"></v-divider>
          
          <div class="card-meta-row">
            <!-- Dozent -->
            <div class="d-flex align-center" style="gap: 8px;">
              <v-icon color="#6b7280" size="20">mdi-account-tie</v-icon>
              <span class="text-body-2 font-weight-medium meta-text">{{ webinar.dozent }}</span>
            </div>

            <!-- Dauer -->
            <div class="d-flex align-center" style="gap: 8px;">
              <v-icon color="#6b7280" size="20">mdi-clock-outline</v-icon>
              <span class="text-body-2 font-weight-medium meta-text-soft">{{ webinar.dauer }} Min</span>
            </div>

            <!-- Termin -->
            <div v-if="webinar.termin" class="d-flex align-center" style="gap: 8px;">
              <v-icon color="#6b7280" size="20">mdi-calendar</v-icon>
              <span class="text-body-2 font-weight-medium meta-text">{{ webinar.termin }}</span>
            </div>
          </div>

          <div v-if="webinar.termine && webinar.termine.length > 0" class="mt-3">
            <v-divider class="mb-2"></v-divider>
            <div class="text-body-2">
              <span class="font-weight-bold meta-text">Nächste Termine:</span>
              <span
                v-for="(item, idx) in webinar.termine"
                :key="item.terminId"
                class="ml-2 meta-text-soft termin-item"
              >{{ item.label }}<template v-if="idx < webinar.termine.length - 1"> ·</template></span>
            </div>
          </div>

          <div v-if="webinar.showStartNow && webinar.startNowUrl" class="mt-4">
            <v-btn
              class="start-now-btn"
              :color="webinar.isDozentTeilnahme || webinar.isAdminTeilnahme ? 'deep-purple-darken-1' : 'success'"
              size="large"
              block
              :href="webinar.startNowUrl"
            >
              {{ webinar.isDozentTeilnahme || webinar.isAdminTeilnahme ? 'jetzt Webinar als Dozent starten' : 'Jetzt Webinar starten' }}
            </v-btn>
          </div>

          <!-- Teilnahmezertifikat: erst nach Teilnahme und abgeschlossenem Termin -->
          <div v-if="isCompleted && webinar.attended && webinar.zertifikatUrl" class="mt-4">
            <v-btn
              class="start-now-btn"
              color="teal-darken-2"
              variant="tonal"
              size="large"
              block
              :href="webinar.zertifikatUrl"
              target="_blank"
              prepend-icon="mdi-certificate-outline"
            >
              Teilnahmezertifikat herunterladen (PDF)
            </v-btn>
          </div>
          
          <!-- Teilnehmer-Anzeige -->
          <div v-if="teilnehmer && teilnehmer.length > 0" class="mt-4">
            <v-divider class="mb-3"></v-divider>
            <div class="text-body-2">
              <span class="font-weight-bold meta-text">Teilnehmer:</span>
              
              <span v-for="t in visibleTeilnehmer" :key="t.wteil_id" class="ml-1">
                <span v-if="t.wteil_pers_type <= 1" class="meta-text">Zahnarzt </span>
                <span v-if="t.wteil_pers_type == 2" class="meta-text">ZFA </span>
                <span class="meta-text-soft"> {{ t.user_ort }}</span>
                <span style="color: #ccc;"> | </span>
              </span>
              
              <span v-if="teilnehmer.length > maxVisible && !showAll" class="ml-2">
                ... und <b class="meta-text">{{ teilnehmer.length - maxVisible }}</b> weitere
                <a @click="toggleShowAll" class="toggle-link">
                  ... mehr anzeigen
                </a>
              </span>
              
              <span v-if="showAll">
                <span v-for="t in hiddenTeilnehmer" :key="t.wteil_id" class="ml-1">
                  <span v-if="t.wteil_pers_type <= 1" class="meta-text">Zahnarzt</span>
                  <span v-if="t.wteil_pers_type == 2" class="meta-text">ZFA</span>
                  <span class="meta-text-soft"> {{ t.user_ort }}</span>
                  <span style="color: #ccc;"> | </span>
                </span>
                <a @click="toggleShowAll" class="toggle-link">
                  ... weniger anzeigen
                </a>
              </span>
            </div>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { defineProps, ref, computed, inject, onMounted } from 'vue';

const props = defineProps({
  webinar: {
    type: Object,
    required: true
  },
  baseStore: {
    type: Object,
    default: null
  }
});

const baseCalls = inject('baseCalls');

// Teilnehmer-Logik
const teilnehmer = ref([]);
const teilnehmerLoaded = ref(false);
const maxVisible = ref(5);
const showAll = ref(false);

const visibleTeilnehmer = computed(() => {
  if (showAll.value) {
    return [];
  }
  return teilnehmer.value.slice(0, maxVisible.value);
});

const hiddenTeilnehmer = computed(() => {
  return teilnehmer.value.slice(maxVisible.value);
});

const webinarDate = computed(() => {
  if (!props.webinar?.startsAt) return null;
  const parsed = new Date(props.webinar.startsAt);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
});

const isUpcoming = computed(() => {
  if (!webinarDate.value) return false;
  return webinarDate.value.getTime() > Date.now();
});

const isCompleted = computed(() => {
  if (!webinarDate.value) return false;
  return webinarDate.value.getTime() <= Date.now();
});

async function loadTeilnehmer() {
  if (teilnehmerLoaded.value) return;
  
  try {
    const data = {
      action: 'getTeilnehmerByTerminId',
      wteil_termin_id: props.webinar.terminId
    };
    const response = await baseCalls.webinarPost(data);
    
    if (response.data.success) {
      teilnehmer.value = response.data.teilnehmer || [];
      teilnehmerLoaded.value = true;
      console.log('✅ Teilnehmer geladen:', teilnehmer.value.length);
    }
  } catch (error) {
    console.error('❌ Fehler beim Laden der Teilnehmer:', error);
  }
}

function toggleShowAll() {
  showAll.value = !showAll.value;
}

// Teilnehmer beim Mount laden
onMounted(() => {
  if (props.webinar.terminId) {
    loadTeilnehmer();
  }
});
</script>

<style scoped>
.hover-card {
  transition: all 0.3s ease;
}

.hover-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(17, 24, 39, 0.08) !important;
}

.card-upcoming {
  border-left: 4px solid #16a34a !important;
  box-shadow: 0 8px 18px rgba(22, 163, 74, 0.1) !important;
}

.card-completed {
  opacity: 0.86;
}

.webinar-badge {
  box-shadow: none !important;
}

.card-title {
  color: #374151 !important;
}

.card-title-wrap {
  flex: 1 1 280px;
  min-width: 0;
}

.meta-text {
  color: #4b5563;
}

.meta-text-soft {
  color: #6b7280;
}

.toggle-link {
  color: #4b5563;
  cursor: pointer;
  text-decoration: underline;
  font-weight: 600;
}

.termin-item {
  display: inline-block;
  white-space: nowrap;
}

.start-now-btn {
  min-height: 52px;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.card-text-pad {
  padding: 16px;
}

.card-body {
  display: flex;
  align-items: flex-start;
}

.card-media {
  margin-right: 16px;
  flex-shrink: 0;
}

.card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.card-meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

@media (max-width: 767px) {
  .card-text-pad {
    padding: 12px;
  }

  .card-media {
    margin-right: 12px;
  }

  .card-title-mobile {
    font-size: 16.5px;
    font-weight: 700;
    line-height: 1.35;
    letter-spacing: 0;
  }

  .card-head {
    gap: 6px 10px;
    margin-bottom: 6px !important;
  }

  .card-meta-row {
    justify-content: flex-start;
    gap: 6px 18px;
  }

  .start-now-btn {
    min-height: 48px;
    font-size: 16px;
    white-space: normal;
  }

  .start-now-btn :deep(.v-btn__content) {
    white-space: normal;
  }
}

/* Alle Rundungen entfernen */
.v-card,
.v-btn,
.v-chip,
.v-avatar {
  border-radius: 0 !important;
}
</style>

